import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { useLocation } from 'react-router-dom';

import MenuIcon from '@material-ui/icons/Menu';
import { TopBar } from './TopBar';
import { PaletteProvider } from './PaletteProvider';
import { PortalFooter } from './PortalFooter';
import { SidebarGroupLabel } from './SidebarGroupLabel';
import { SidebarSection } from './SidebarSection';
import { SidebarNavItem } from './SidebarNavItem';
import {
  ShelfFilterProvider,
  ShelfHeader,
  ShelfNoMatches,
} from './SidebarShelf';
import { useCurrentSection } from './TopNav';
import {
  AccessIcon,
  ApiIcon,
  AreasIcon,
  AskIcon,
  CatalogIcon,
  DeploymentsIcon,
  DocsIcon,
  HouseIcon,
  JamIcon,
  ResponsibilityIcon,
  ScorecardsIcon,
  SecurityIcon,
  StatusIcon,
  TerminalIcon,
} from './navIcons';
import { SidebarStarred } from './SidebarStarred';
import { useNavCounts } from './useNavCounts';
import { useDocsSiteCount } from '../docs/useDocsSiteCount';
import {
  Sidebar,
  SidebarGroup,
  SidebarPage,
  SidebarPinStateProvider,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarPinState,
} from '@backstage/core-components';

/**
 * The navigation column, grouped the way the target picture groups it
 * (DX-302).
 *
 * The groups sort by what a reader is doing, not by what the entries are
 * about. Main is what they do — arrive, ask. Discover is what they look up:
 * registers of what exists, whether the subject is a service, a document or a
 * person. Operate is what has a live state, the things that can be red this
 * morning and green this afternoon. Tools is what they connect their own
 * machine to. Starred is theirs.
 *
 * Main is only drawn where the bar above the page is not (DX-546): Home and
 * Ask are sections of that bar, and on a screen wide enough to show it the
 * column repeated them one hand-width below. On a phone the bar's sections
 * are hidden, and the column is then the only way to them. Create left the
 * column with the same ticket — the portal's GitHub App cannot create a
 * repository yet (DX-238), so the entry led to a run that stops; it comes
 * back with the write access. The notifications bell left too: nothing in
 * the portal sends a notification (DX-551 decides what should), and a bell
 * that never rings is the defect DX-275 measured.
 *
 * That division is why Responsibility and the Areas under it sit in Discover
 * rather than beside Status. "Who owns what" is looked up the same way "which
 * services exist" is looked up; it changes every few months, and putting it
 * under Operate would blunt the one thing that group's dot is for.
 *
 * The target picture carries neither of those two, and they are here anyway.
 * They are one register entered two ways — the map and the four fields render
 * from a single array transcribed from the organisation's own Figma board
 * (DX-180) — so dropping the fields would remove half a document the portal
 * does not own. That departure is Michael's to confirm.
 *
 * MUI v4 only — no MUI v5.
 */

/**
 * The frame the public revenexx portals share (DX-352): a bar floating above
 * a framed page on a tinted ground, and a footer under it. The geometry and
 * colours are tokens (`--ui-shell-*`, `--ui-frame-*`, `--ui-ground*` in
 * tokens.css), taken from the Developer Portal's source.
 *
 * It is set here, once, rather than on each page: every plugin page — the
 * catalog, the entity pages, the scaffolder, search, TechDocs, the API docs —
 * renders its own Page, Header and Content, and a frame drawn per page would
 * miss the next plugin to be added.
 */
const useStyles = makeStyles(theme => ({
  ground: {
    position: 'fixed',
    inset: 0,
    zIndex: -1,
    overflow: 'hidden',
    pointerEvents: 'none',
    background: 'var(--ui-ground)',
  },
  // The Developer Portal's `.docs-backdrop-noise` and `-glow`, verbatim.
  noise: {
    position: 'absolute',
    inset: 0,
    opacity: 0.2,
    mixBlendMode: 'overlay',
    backgroundImage: "url('/noise.svg')",
    backgroundSize: '200px 200px',
  },
  glow: {
    position: 'absolute',
    top: 0,
    left: '33.333%',
    width: '60rem',
    height: '20rem',
    opacity: 'var(--ui-ground-glow-opacity)',
    backgroundImage: 'var(--ui-ground-glow)',
    maskImage: 'radial-gradient(farthest-side at top, white, transparent)',
    WebkitMaskImage:
      'radial-gradient(farthest-side at top, white, transparent)',
  },
  shell: {
    paddingTop: 'var(--ui-shell-top)',
    paddingInline: 'var(--ui-shell-gutter)',
  },
  frame: {
    position: 'relative',
    maxWidth: 'var(--ui-shell-max)',
    margin: '0 auto var(--ui-shell-inset)',
    /*
      Bar, frame, gap and footer add up to the window, so the page does not
      scroll — the content inside the frame does, as on the portals. The
      floor keeps a short window usable; below it the page scrolls instead.
    */
    height:
      'calc(100svh - var(--ui-shell-top) - var(--ui-footer-measured, var(--ui-footer-height)) - var(--ui-shell-inset))',
    minHeight: '20rem',
    boxSizing: 'border-box',
    overflow: 'hidden',
    borderRadius: 'var(--ui-frame-radius)',
    border: '1px solid var(--ui-frame-border)',
    outline: '1px solid var(--ui-frame-outline)',
    background: 'var(--ui-frame-bg)',
    backdropFilter: 'blur(4px)',
    /*
      The one line that puts Backstage's sidebar inside the frame. The sidebar
      is `position: fixed` against the window, and layout containment makes
      this element the box a fixed descendant is placed in — so it sits at the
      frame's left edge, full height of the frame, without overriding how
      Backstage positions it. Dialogs and menus are unaffected: Material-UI
      renders them into a portal at the end of <body>, outside the frame.
    */
    contain: 'layout',
    /*
      On a phone the frame steps aside, as the public portals' does: the page
      is the window's full width and scrolls as one. It also has to, for
      Backstage's sake — below 600px its menu is a bar fixed to the bottom of
      the WINDOW, and inside a containing frame it was fixed to the frame's
      bottom instead, out of line with the overlay it opens.
    */
    [theme.breakpoints.down('xs')]: {
      height: 'auto',
      minHeight: 0,
      overflow: 'visible',
      contain: 'none',
      backdropFilter: 'none',
    },
    /*
      Printed, a frame one window tall prints one window of the page. Backstage
      resets its own pages for print; the frame has to step aside the same
      way, or everything below the fold is cut off.
    */
    '@media print': {
      height: 'auto',
      overflow: 'visible',
      contain: 'none',
      backdropFilter: 'none',
      border: 0,
      outline: 0,
    },
  },
}));

/**
 * A page that reads one document: the TechDocs reader, standalone or as an
 * entity's Docs tab. The list of all documentation (`/docs`) is not one.
 */
const isDocumentPage = (pathname: string) =>
  /^\/docs\/[^/]+\/[^/]+\/[^/]+/.test(pathname) ||
  /^\/catalog\/[^/]+\/[^/]+\/[^/]+\/docs(\/|$)/.test(pathname);

/**
 * Collapses the sidebar to its icons on documentation pages (DX-352, point 4).
 *
 * A document page carries the portal's navigation, the document's own and its
 * table of contents — one column more than any public portal — and inside the
 * frame the reading column would lose that width. Collapsed, the sidebar still
 * opens on hover, as it does whenever it is not pinned.
 *
 * It overrides the pin state for everything inside SidebarPage and leaves the
 * reader's stored choice untouched: back on any other page the sidebar is as
 * they left it. The content has to see the override as well as the sidebar —
 * the TechDocs reader places its own navigation column by the pin state
 * (plugin-techdocs, `transformers/styles/rules/layout`), and while it read
 * "pinned" it drew that column 224px in, over the start of the document.
 * SidebarPage's own padding cannot see this state at all, so revenexxTheme.ts
 * pads by `data-shell-reading` on the frame instead.
 */
const ReadingPinState = ({
  reading,
  children,
}: {
  reading: boolean;
  children: ReactNode;
}) => {
  const pinState = useSidebarPinState();
  /*
    Memoised on the values, not on the object: SidebarPage hands down a new
    object — and a new toggle function — on every render, and the TechDocs
    reader rebuilds its whole stylesheet whenever this one's identity changes,
    which was every navigation and every count that arrived. The toggle is
    left out of the dependencies on purpose: it only flips `isPinned`, so the
    one captured alongside the current `isPinned` is always the right one.
  */
  const { isPinned, isMobile } = pinState;
  const value = useMemo(
    () => ({ ...pinState, isPinned: isPinned && !reading }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPinned, isMobile, reading],
  );
  return (
    <SidebarPinStateProvider value={value}>{children}</SidebarPinStateProvider>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const counts = useNavCounts();
  const docsCount = useDocsSiteCount();
  const classes = useStyles();
  const reading = isDocumentPage(useLocation().pathname);
  // The column's title: the bar's section, or the product on a page that
  // belongs to none (Create, APIs, the tools).
  const section = useCurrentSection();
  // Where the bar's sections are hidden (TopNav), the column carries Main.
  // noSsr: without it the first render reads false and Home and Ask pop into
  // a narrow column one frame later.
  const barHidden = useMediaQuery(useTheme().breakpoints.down('sm'), {
    noSsr: true,
  });

  return (
    <PaletteProvider>
      <div className={classes.ground} aria-hidden="true">
        <div className={classes.noise} />
        <div className={classes.glow} />
      </div>
      <TopBar />
      <div className={classes.shell}>
        <div
          className={classes.frame}
          data-shell-reading={reading ? '' : undefined}
        >
          <SidebarPage>
            <ReadingPinState reading={reading}>
              <ShelfFilterProvider>
                <Sidebar>
                  <SidebarGroup label="Menu" icon={<MenuIcon />}>
                    <ShelfHeader title={section ?? 'Backstage'} />
                    {/* Home and Ask only where the bar above the page is
                        hidden — the same breakpoint as the bar's own
                        sections, so one of the two always carries them and
                        never both (DX-546). */}
                    {barHidden && (
                      <>
                        <SidebarGroupLabel>Main</SidebarGroupLabel>
                        <SidebarNavItem
                          icon={HouseIcon}
                          to="/"
                          text="Home"
                          end
                        />
                        {/*
                          Ask is the chat over atlas and the TechDocs that
                          PE-66 built as the Docs Assistant. DX-357 made the
                          two one entry, so there is no second row for the
                          same question.
                        */}
                        <SidebarNavItem icon={AskIcon} to="/ask" text="Ask" />
                      </>
                    )}

                    {/*
              The registers: what exists, and where it is written down. Each
              entry is a list a reader looks something up in — components,
              documents, API specs, people.

              Catalog and Docs carry a count, because the number is part of
              what the entry tells you. Decisions is absent entirely rather
              than present without one: its source is the ADR index (DX-310),
              which does not exist yet, and an entry leading to nothing is the
              defect DX-275 measured in the old sidebar.
            */}
                    <SidebarGroupLabel>Discover</SidebarGroupLabel>
                    <SidebarNavItem
                      icon={CatalogIcon}
                      to="/catalog"
                      text="Catalog"
                      count={counts.catalog}
                    />
                    <SidebarNavItem
                      icon={DocsIcon}
                      to="/docs"
                      text="Docs"
                      count={docsCount}
                    />
                    <SidebarNavItem icon={ApiIcon} to="/api-docs" text="APIs" />
                    {/*
              The responsibility map and the four fields it is divided into —
              one register, entered two ways (DX-180).

              They are not two features that happen to sit together. The map,
              the fields and the eleven areas under them are ONE array,
              transcribed from the Figma board „Verantwortungsbereiche": the
              map renders all of it, and each field's page renders its share
              plus the services the catalog says that field owns. Removing the
              fields while keeping the map would take away one of two views of
              the same organisational document, which is not a navigation
              decision to make on a portal's own account.

              The target picture carries neither. Keeping them is a deliberate
              departure from it, and it is Michael's to confirm — the document
              behind them is the organisation's, not the portal's.
            */}
                    <SidebarNavItem
                      icon={ResponsibilityIcon}
                      to="/verantwortung"
                      text="Responsibility"
                    />
                    <SidebarSection
                      text="Areas"
                      icon={<AreasIcon />}
                      children={[
                        {
                          title: 'Platform',
                          subtitle: 'Infra, CI/CD & Core Systems',
                          to: '/platform',
                        },
                        {
                          title: 'Product',
                          subtitle: 'Features, Shop & Cockpit UI',
                          to: '/product',
                        },
                        {
                          title: 'DX',
                          subtitle: 'Tooling, SDKs & Templates',
                          to: '/dx',
                        },
                        {
                          title: 'Consulting',
                          subtitle: 'Client Projects & Go-Lives',
                          to: '/consulting',
                        },
                      ]}
                    />

                    {/*
              What has a live state. Every one of these can be different an
              hour from now, which is what separates them from the registers
              above — and what will make the dot beside Status mean something
              once the status service's probes (DX-308) set it.

              All four pages are placeholders until their own issues build
              them; the entries exist here so the shell is built once rather
              than once per sibling issue.
            */}
                    <SidebarGroupLabel>Operate</SidebarGroupLabel>
                    <SidebarNavItem
                      icon={StatusIcon}
                      to="/status"
                      text="Status"
                    />
                    <SidebarNavItem
                      icon={DeploymentsIcon}
                      to="/deployments"
                      text="Deployments"
                    />
                    <SidebarNavItem
                      icon={ScorecardsIcon}
                      to="/scorecards"
                      text="Scorecards"
                    />
                    <SidebarNavItem
                      icon={SecurityIcon}
                      to="/security"
                      text="Security"
                    />
                    <SidebarNavItem
                      icon={AccessIcon}
                      to="/access"
                      text="Access"
                    />

                    {/*
              What a reader connects their own machine to. The target picture
              pairs these two on its developer-tools page, which is the
              argument for pairing them here: JAM is the local runtime, MCP and
              the CLI are how Claude and the terminal reach the portal.
            */}
                    <SidebarGroupLabel>Tools</SidebarGroupLabel>
                    <SidebarNavItem icon={JamIcon} to="/jam" text="JAM" />
                    <SidebarNavItem
                      icon={TerminalIcon}
                      to="/mcp"
                      text="MCP & CLI"
                    />

                    <SidebarStarred />
                    <ShelfNoMatches />

                    <SidebarScrollWrapper />
                  </SidebarGroup>
                  <SidebarSpace />
                  {/* Settings is reached from the account menu in the top bar, where
              the other revenexx portals put it, so it is no longer a row of
              its own down here. The page itself stays: it is the only place
              that shows which catalog entities a reader owns, which is what
              explains an empty "components you own" card. The notifications
              bell stood here until DX-546; /notifications is still routed,
              for the day something sends one (DX-551). */}
                </Sidebar>
              </ShelfFilterProvider>
              {children}
            </ReadingPinState>
          </SidebarPage>
        </div>
      </div>
      <PortalFooter />
    </PaletteProvider>
  );
};
